import axios from 'axios';
import CryptoJS from 'crypto-js';

class OKXAPI {
  constructor(apiKey, secretKey, passphrase) {
    this.apiKey = apiKey;
    this.secretKey = secretKey;
    this.passphrase = passphrase;
    this.baseUrl = 'https://www.okx.com';
  }

  // 生成签名
  generateSignature(timestamp, method, requestPath, body = '') {
    const message = timestamp + method.toUpperCase() + requestPath + body;
    const signature = CryptoJS.HmacSHA256(message, this.secretKey);
    return CryptoJS.enc.Base64.stringify(signature);
  }

  // 创建认证头部
  createAuthHeaders(method, requestPath, body = '') {
    const timestamp = new Date().toISOString();
    const signature = this.generateSignature(timestamp, method, requestPath, body);

    return {
      'OK-ACCESS-KEY': this.apiKey,
      'OK-ACCESS-SIGN': signature,
      'OK-ACCESS-TIMESTAMP': timestamp,
      'OK-ACCESS-PASSPHRASE': this.passphrase,
      'Content-Type': 'application/json'
    };
  }

  // 获取账户余额
  async getAccountBalance(ccy = null) {
    let requestPath = '/api/v5/account/balance';
    if (ccy) {
      requestPath += `?ccy=${ccy}`;
    }

    const headers = this.createAuthHeaders('GET', requestPath);
    
    try {
      const response = await axios.get(this.baseUrl + requestPath, { headers });
      return response.data;
    } catch (error) {
      console.error('Error fetching OKX account balance:', error.response?.data || error.message);
      throw error;
    }
  }

  // 获取持仓信息
  async getPositions(instType = null, instId = null) {
    let params = [];
    if (instType) params.push(`instType=${instType}`);
    if (instId) params.push(`instId=${instId}`);
    
    let requestPath = '/api/v5/account/positions';
    if (params.length > 0) {
      requestPath += '?' + params.join('&');
    }

    const headers = this.createAuthHeaders('GET', requestPath);
    
    try {
      const response = await axios.get(this.baseUrl + requestPath, { headers });
      return response.data;
    } catch (error) {
      console.error('Error fetching OKX positions:', error.response?.data || error.message);
      throw error;
    }
  }

  // 获取余币宝余额
  async getSavingsBalance(ccy = null) {
    let params = [];
    if (ccy) params.push(`ccy=${ccy}`);
    
    let requestPath = '/api/v5/finance/savings/balance';
    if (params.length > 0) {
      requestPath += '?' + params.join('&');
    }

    const headers = this.createAuthHeaders('GET', requestPath);
    
    try {
      const response = await axios.get(this.baseUrl + requestPath, { headers });
      return response.data;
    } catch (error) {
      // If the endpoint doesn't exist or is not accessible, return empty data
      console.warn('OKX savings API not accessible:', error);
      return { code: '0', data: [] }; // Return empty data instead of throwing
    }
  }

  // 获取交易产品信息
  async getInstruments(instType, instFamily = null, instId = null) {
    let params = [`instType=${instType}`];
    if (instFamily) params.push(`instFamily=${instFamily}`);
    if (instId) params.push(`instId=${instId}`);
    
    const requestPath = '/api/v5/account/instruments?' + params.join('&');

    const headers = this.createAuthHeaders('GET', requestPath);
    
    try {
      const response = await axios.get(this.baseUrl + requestPath, { headers });
      return response.data;
    } catch (error) {
      console.error('Error fetching OKX instruments:', error.response?.data || error.message);
      throw error;
    }
  }

  // 获取未成交订单（挂单）
  async getOpenOrders(instType = null, instId = null, ordType = null) {
    let params = [];
    if (instType) params.push(`instType=${instType}`);
    if (instId) params.push(`instId=${instId}`);
    if (ordType) params.push(`ordType=${ordType}`);
    
    let requestPath = '/api/v5/trade/orders-pending';
    if (params.length > 0) {
      requestPath += '?' + params.join('&');
    }

    const headers = this.createAuthHeaders('GET', requestPath);
    
    try {
      const response = await axios.get(this.baseUrl + requestPath, { headers });
      return response.data;
    } catch (error) {
      console.error('Error fetching OKX open orders:', error.response?.data || error.message);
      throw error;
    }
  }
}

export default OKXAPI;